import React, { useEffect, useState } from 'react';
import { Button } from '../primitives/Button';
import { assetUri } from '../utils/assetUri';
import { useAuth } from '../../../context/AuthContext';
import logoDarkRaw from '../../../assets/images/landing-v2/source/logo-dark-hero.png';

const logoDark = assetUri(logoDarkRaw);

// `target` starting with '#' scrolls to the section on this page; everything
// else is treated as a real route the browser navigates to.
const defaultNavLinks = [
  { title: 'How it works', target: '#how-it-works' },
  { title: 'Score Breakdown', target: '#score-breakdown' },
  { title: 'Pricing', target: '/pricing' },
  { title: 'Testimonials', target: '#testimonials' },
  { title: 'FAQ', target: '#faq' },
];

function scrollToHash(hash) {
  const id = hash.replace('#', '');
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function HamburgerIcon({ className = '' }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className={className} aria-hidden="true">
      <rect x="3" y="6" width="18" height="2" rx="1" fill="currentColor" />
      <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor" />
      <rect x="3" y="16" width="18" height="2" rx="1" fill="currentColor" />
    </svg>
  );
}

function CloseIcon({ className = '' }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className={className} aria-hidden="true">
      <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function Header({
  onCtaPress,
  onNavPress,
  onLoginPress,
  ctaTitle = 'Get Started',
  ctaHref = '/onboarding',
  homeHref = '#top',
  navLinks = defaultNavLinks,
  // /pricing surfaces this so the variant CTA sits next to the hamburger and
  // the visitor never has to open the menu to convert. Landing leaves it
  // false so the mobile pill stays minimal (logo + hamburger).
  mobileShowCta = false,
}) {
  const { user } = useAuth() || {};
  const isAuthed = !!user?.id;
  const loginLabel = isAuthed ? 'Dashboard' : 'Login';
  const loginHref = isAuthed ? '/home' : '/auth';
  const [menuOpen, setMenuOpen] = useState(false);

  function handleNavClick(e, target) {
    onNavPress?.(target);
    if (target.startsWith('#')) {
      e.preventDefault();
      scrollToHash(target);
    }
    // Real routes (e.g. /pricing) fall through and let the browser navigate.
    setMenuOpen(false);
  }

  function handleCtaInMenu(e) {
    onCtaPress?.(e);
    setMenuOpen(false);
  }

  function handleLoginInMenu(e) {
    onLoginPress?.(e);
    setMenuOpen(false);
  }

  // Lock body scroll while the mobile menu is open. The lp-v2-root scroller
  // owns scroll on this surface, so toggling its overflow is what actually
  // freezes the page beneath the overlay.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.querySelector('.lp-v2-root');
    if (!root) return undefined;
    if (menuOpen) {
      const prev = root.style.overflow;
      root.style.overflow = 'hidden';
      return () => { root.style.overflow = prev; };
    }
    return undefined;
  }, [menuOpen]);

  return (
    <header className="absolute top-4 inset-x-0 z-50 flex justify-center px-6 md:px-0 pointer-events-none">
      {/* Mobile: frosted-glass pill - small black logo + hamburger inside a
          translucent rounded bar. backdrop-blur gives the Apple-glass effect;
          bg-white/55 keeps it readable over both the bright hero photo and
          the white sections below. */}
      <div className="md:hidden pointer-events-auto w-full flex items-center justify-between gap-2 bg-white/55 backdrop-blur-xl backdrop-saturate-150 border border-white/40 rounded-pill-sm shadow-header-pill pl-4 pr-2 py-2">
        <a
          href={homeHref}
          aria-label="DooCars home"
          className="flex items-center shrink-0"
          onClick={(e) => handleNavClick(e, homeHref)}
        >
          <img src={logoDark} alt="DooCars" className="h-[14px] w-auto" />
        </a>
        <div className="flex items-center gap-1">
          {mobileShowCta ? (
            <a
              href={ctaHref}
              onClick={onCtaPress}
              className="inline-flex items-center font-display font-bold text-[13px] leading-tight text-on-dark bg-ink-900 rounded-full px-3 py-1.5 whitespace-nowrap"
            >
              {ctaTitle}
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
            className="text-ink-900 p-2"
          >
            <HamburgerIcon />
          </button>
        </div>
      </div>

      {/* Desktop / tablet: original floating pill. */}
      <div className="hidden md:flex pointer-events-auto w-full max-w-[1200px] bg-surface rounded-[32px] items-center justify-between pl-6 pr-1 py-1 shadow-header-pill">
        <a href={homeHref} aria-label="DooCars home" className="flex items-center shrink-0" onClick={(e) => handleNavClick(e, homeHref)}>
          <img src={logoDark} alt="DooCars" className="h-5 w-auto" />
        </a>
        <nav aria-label="Primary" className="flex items-center gap-8 font-sans font-normal text-sm leading-[1.6] text-ink-900">
          {navLinks.map((link) => (
            <a
              key={link.target}
              href={link.target}
              onClick={(e) => handleNavClick(e, link.target)}
              className="hover:text-ink-500 transition-colors duration-fast whitespace-nowrap"
            >
              {link.title}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a
            href={loginHref}
            onClick={onLoginPress}
            className="inline-flex items-center font-sans font-medium text-sm leading-[1.6] text-ink-900 hover:text-ink-500 transition-colors duration-fast px-3 py-2 whitespace-nowrap"
          >
            {loginLabel}
          </a>
          <Button variant="dark" as="a" href={ctaHref} onClick={onCtaPress}>{ctaTitle}</Button>
        </div>
      </div>

      {/* Mobile menu overlay - full-screen sheet with nav links + Login + Get
          Started, mirroring the v1 modal pattern. */}
      {menuOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          className="md:hidden pointer-events-auto fixed inset-0 z-50 bg-surface flex flex-col"
        >
          <div className="flex items-center justify-between px-6 py-4">
            <a
              href={homeHref}
              aria-label="DooCars home"
              onClick={(e) => handleNavClick(e, homeHref)}
            >
              <img src={logoDark} alt="DooCars" className="h-[14px] w-auto" />
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="text-ink-900 p-2 -mr-2"
            >
              <CloseIcon />
            </button>
          </div>
          <nav
            aria-label="Mobile primary"
            className="flex-1 flex flex-col items-center justify-center gap-6 px-6"
          >
            {navLinks.map((link) => (
              <a
                key={link.target}
                href={link.target}
                onClick={(e) => handleNavClick(e, link.target)}
                className="font-display font-bold text-2xl leading-[1.4] text-ink-900"
              >
                {link.title}
              </a>
            ))}
          </nav>
          <div className="flex flex-col gap-3 px-6 pb-10 pt-4">
            <a
              href={loginHref}
              onClick={handleLoginInMenu}
              className="inline-flex items-center justify-center w-full font-display font-medium text-base leading-[1.6] text-ink-900 bg-surface border border-ink-900 rounded-full py-4"
            >
              {loginLabel}
            </a>
            <Button variant="dark" as="a" href={ctaHref} onClick={handleCtaInMenu} className="w-full">
              {ctaTitle}
            </Button>
          </div>
        </div>
      ) : null}
    </header>
  );
}
